import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { useInfiniteHits, useInstantSearch, useStats } from "react-instantsearch";
import { isValidArray } from "../../Layout/Common/common";
import { getUniqueFamiliesByLowestPrice, hasActiveRefinements } from "../../Layout/Common/algoliaInstantLib";
import { ProductCardAlgoliaList } from "../../Layout/Common/productCardAlgoliaList";
import { ProductCardAlgolia } from "../../Layout/Common/productCardAlgolia";
import { LazyCard } from "../../Layout/Common/lazyCard";
import { useAuth } from "../../Context/AuthContext";
import { DynamicLoader } from "./dynamicLoader";
import { ProductSkeletonLoader } from "./productSkeletonLoader";
import { NoResultFound } from "./noResultFound";
import { useGridScrollRestore } from "../../Context/hook/useGridScrollRestore";
import { primeProductPreview } from "../ProductHybrid/productPreviewCache";

export const SearchResultItems = ({ distinctFamilyMode = false } = {}) => {
  const { isGridView } = useContext(GlobalContext);
  const { priceCode } = useAuth();
  const { status, indexUiState } = useInstantSearch();
  const { nbHits } = useStats();
  const { currentPageHits, showMore, results, isLastPage } = useInfiniteHits();
  const [isPaginating, setIsPaginating] = useState(false);
  const [accumulatedHits, setAccumulatedHits] = useState([]);
  const [loadedPageCount, setLoadedPageCount] = useState(0);
  const [renderedPageCount, setRenderedPageCount] = useState(0);
  const loadedPagesRef = useRef(new Set());
  const hasRenderedHits = useRef(false);

  const activeCategoryKey = JSON.stringify(indexUiState.hierarchicalMenu?.["categories.lvl0"] ?? null);
  const activeCategory = useMemo(() => JSON.parse(activeCategoryKey), [activeCategoryKey]);
  const hasRefinements = hasActiveRefinements(indexUiState);
  const page = results?.page ?? 0;

  useEffect(() => {
    if (!isValidArray(currentPageHits)) return;
    if (page === 0) {
      loadedPagesRef.current = new Set([0]);
      setAccumulatedHits(currentPageHits);
      setLoadedPageCount(1);
      return;
    }
    if (loadedPagesRef.current.has(page)) return; // page re-delivered - don't double-append
    loadedPagesRef.current.add(page);
    setAccumulatedHits((prev) => [...prev, ...currentPageHits]);
    setLoadedPageCount(loadedPagesRef.current.size);
  }, [currentPageHits, page]);

  const itemList = useMemo(
    () =>
      getUniqueFamiliesByLowestPrice(accumulatedHits, priceCode, activeCategory, {
        familyByProductCount: distinctFamilyMode && !hasRefinements,
      }),
    [accumulatedHits, priceCode, activeCategory, distinctFamilyMode, hasRefinements],
  );

  useEffect(() => {
    itemList.forEach(primeProductPreview);
    setRenderedPageCount(loadedPageCount);
  }, [itemList, loadedPageCount]);

  useEffect(() => {
    if (isValidArray(accumulatedHits)) hasRenderedHits.current = true;
    console.log("results", accumulatedHits);
  }, [accumulatedHits]);

  useEffect(() => {
    if (status === "idle") setIsPaginating(false);
  }, [status, page]);

  const waitingForRender = isPaginating || status !== "idle" || renderedPageCount !== loadedPageCount;

  const loadNextPage = () => {
    setIsPaginating(true);
    showMore();
  };

  const handleShowMore = () => {
    if (waitingForRender) return;
    loadNextPage();
  };
  const { isRestoring, onCardClickCapture } = useGridScrollRestore({
    page,
    isLastPage,
    status,
    showMore: loadNextPage,
    hasHits: isValidArray(itemList),
  });

  if (status !== "idle" && !hasRenderedHits.current) return <ProductSkeletonLoader />;
  if (nbHits === 0 && status === "idle") return <NoResultFound />;
  if (!isValidArray(itemList)) return null;

  const cardKey = (hit, index) => hit.key ?? `item-${index}`;

  const showMoreLoader = isLastPage === false && (
    <DynamicLoader
      isGridView={isGridView}
      isLastPage={isLastPage}
      showMore={handleShowMore}
      currentPage={page}
      paused={isRestoring || waitingForRender}
    />
  );

  if (isGridView) {
    return (
      <div className="col-lg-12" style={{ opacity: status !== "idle" && !isPaginating ? 0.5 : 1 }}>
        <div className="productCard-wrapper">
          {itemList.map((hit, index) => (
            <LazyCard
              data-view={isGridView}
              className="productCard"
              id={`product-${cardKey(hit, index)}`}
              data-insights-object-id={hit.productId}
              key={cardKey(hit, index)}
              onClickCapture={onCardClickCapture}
            >
              <ProductCardAlgolia data={hit} catFilter={false} />
            </LazyCard>
          ))}
        </div>
        {showMoreLoader}
      </div>
    );
  }

  return (
    <Fragment>
      {itemList.map((hit, index) => (
        <LazyCard
          data-view={isGridView}
          className="search-row rowProducts-list position-relative"
          id={`product-${cardKey(hit, index)}`}
          data-insights-object-id={hit.productId}
          key={cardKey(hit, index)}
          onClickCapture={onCardClickCapture}
        >
          <ProductCardAlgoliaList data={hit} index={hit.productId} />
        </LazyCard>
      ))}
      {showMoreLoader}
    </Fragment>
  );
};
